import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from "../../config";
import Table from '../../components/table';
import axios from 'axios';
import moment from 'moment';
import DropDown from '../../components/select';
import { BarChart, Bar } from 'recharts';
import authStorage from "../../utils/authStorage";
import {
    aggregateZoneTotals,
    buildParticipantRecord,
    buildSummaryGoalsRows,
    summaryGoalsFileName,
    summaryGoalsSheetName,
    CATEGORY_ACHIEVED,
    CATEGORY_SHORTFALL,
} from "../../helper/summaryGoalsReport";
import downloadExcelSheet from "../../helper/downloadExcel";

const CompanyReport = () => {

    useEffect(() => {
        let isAdmin = ['admin'].includes(authStorage.getItem('roles'));
            
        // If roles is not admin, redirect to dashboard page
        if (!isAdmin) {
            window.location = window.location.origin + '/dashboard';
            return false;
        }
    });

    const navigate = useNavigate();
    const [companyList, setCompanyList] = useState([]);
    const [userList, setUserList] = useState([]);
    const [rowHeaderData, setRowHeaderData] = useState([]);
    const [firstSelected, setFirstSelected] = useState('');
    const [selected, setSelected] = useState();
    const [userDetails, setUserDetails] = useState({});
    const [totalUserAcheivedGoal, setTotalUserAcheivedGoal] = useState([]);
    // Per-participant report rows for the selected company and month.
    const [participants, setParticipants] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        offset: 0,
        itemsPerPage: 10,
        totalCount: 0,
    });
    const [loading, setLoading] = useState([]);

    const [discount, setDiscount] = useState("");

    const companyLabel = (userDetails?.companyName || 'Company').replace(/[^\w-]+/g, '-');

    /*const downloadBillingReport = () => {
        const userRange = userDetails?.userRange || "";  //ensures it's a string even if userRange is undefined or null
        const updatedUserRange = userRange.replace("-", " to ");
        
        handleCsvDownload([{
            "Company Name": userDetails?.companyName,
            "User Range": updatedUserRange,
            "Price Per User": userDetails?.price,
            "Expected CFT User": userDetails?.expectedCftUser,
            "Expected Bill Amount": userDetails?.price && userDetails?.expectedCftUser ? 
                                        Math.round(parseFloat(userDetails.price) * parseInt(userDetails.expectedCftUser, 10)) : 0,
            "Active CFT User": userDetails?.userCount,
            "Total Bill Amount": userDetails?.price && userDetails?.userCount ? 
                                    Math.round(parseFloat(userDetails.price) * parseInt(userDetails.userCount, 10)) : 0,
        }], "billing");
    };
    */

    const downloadBillingReport = () => {
        const userRange = userDetails?.userRange || "";
        const updatedUserRange = userRange.replace("-", " to ");

        const price = parseFloat(userDetails?.price || 0);
        const discountPercent = discount ? parseFloat(discount) : 0;

        // Apply discount only if provided
        const finalPrice =
            discountPercent > 0
            ? price - (price * discountPercent) / 100
            : price;

        const expectedUsers = parseInt(userDetails?.expectedCftUser || 0, 10);
        const activeUsers = parseInt(userDetails?.userCount || 0, 10);

        // Header row then one value row, so the headings can be bolded.
        downloadExcelSheet({
            rows: [
                [
                    "Company Name", "User Range", "Price Per User", "Discount (%)",
                    "Expected CFT User", "Expected Bill Amount", "Active CFT User",
                    "Total Bill Amount",
                ],
                [
                    userDetails?.companyName || '-',
                    updatedUserRange,
                    finalPrice,                        // no rounding
                    discountPercent || "N/A",
                    expectedUsers,
                    finalPrice * expectedUsers,        // no rounding
                    activeUsers,
                    finalPrice * activeUsers,          // no rounding
                ],
            ],
            boldRows: [0],
            fileName: `${companyLabel}-${moment().format('MMM-YYYY')}-Billing-Report.xlsx`,
            sheetName: 'Billing',
        });
    };

    /**
     * Summary & Goals report, downloaded per category so the recognition list and
     * the follow-up list can be handed out separately:
     *   CATEGORY_ACHIEVED  — reached their monthly goal
     *   CATEGORY_SHORTFALL — produced qualifying activity but fell short
     *
     * Both files carry the same header, summary and device breakdown for the whole
     * connected population, and differ only in which participants are detailed.
     * Connected members who produced no qualifying minutes are the only ones
     * absent from every detail table; they stay in the summary so the totals
     * reconcile against the participant count.
     */
    const downloadUserReport = (category) => {
        const { rows, boldRows, boldLabelRows } = buildSummaryGoalsRows({
            companyName: userDetails?.companyName,
            participants,
            category,
        });
        downloadExcelSheet({
            rows,
            boldRows,
            boldLabelRows,
            fileName: summaryGoalsFileName(userDetails?.companyName, category),
            sheetName: summaryGoalsSheetName(category),
        });
    };

    const achieverCount = participants.filter((r) => r.achievedGoal).length;
    const shortfallCount = participants.filter(
        (r) => !r.achievedGoal && r.hasQualifiedActivity
    ).length;

    const getCompanyList = () => {
        setLoading(true);
        axios({
            headers: {
                "x-access-token": authStorage.getItem("auth-token")
            },
            url: `${BASE_URL}/admin/company`,
            method: 'get',
        }).then((res) => {  
            setCompanyList(res?.data?.data || []);
            setFirstSelected(res?.data?.data?.[0]);
            setPagination({ ...pagination, totalCount: res?.data?.count || 0 });
        });
    };

    const getCompanyReport = () => {
        axios({
            headers: {
                "x-access-token": authStorage.getItem("auth-token"),
            },
            url: `${BASE_URL}/admin/company/report/${selected?._id}`, //your url
            method: "GET",
        }).then((res) => {
            setUserDetails(res?.data?.data);
        });
    };

    const getCompanyUsers = () => {
        axios({
            headers: {
                "x-access-token": authStorage.getItem("auth-token"),
            },
            url: `${BASE_URL}/admin/company/user/list/${selected?._id}`, //your url
            method: "GET",
        }).then((res) => {
            console.log("comapny report fucntion calln : ",res?.data )
            setUserList(res?.data?.data);
        });
    };

    const userTableRowTitle = [
        { title: "Date", key: "date", formatData: (data) => moment(data).format('DD MMM YYYY') },
        { title: "Start Time", key: "startTime" },
        { title: "End Time", key: "endTime" },
        { title: "Booked By", key: "user", formatData: (data) => data ? data?.email : '' },
    ];

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth', // Optional: adds smooth scrolling animation
        });
        setRowHeaderData(userTableRowTitle);
        getCompanyList();
    }, []);

    useEffect(() => {
        if (firstSelected?._id) {
            getCompanyReport();
            getCompanyUsers();
        }
    }, [selected]);

    const processUser = async (data) =>
        buildParticipantRecord(data, await getUserActivityRecord(data));

    useEffect(() => {
        // let completedUserGoalList = [];
        // userList?.map(async (data) => {
        //     let userRecord = await getUserActivityRecord();
        //     let { vigorous, moderate, light } = userRecord?.tempTimeConsumed;
        //     let tempMonthlyTarget = 0;
        //     let totalTime = 600 + moderate;
        //     if (vigorous === 0) {
        //         tempMonthlyTarget = 600;
        //     }
        //     if (vigorous >= 1) {
        //         tempMonthlyTarget = 300;
        //     }
        //     if ((tempMonthlyTarget - totalTime) < 0) {
        //         console.log(data, '+++++++==');
        //         completedUserGoalList.push(data);
        //     }
        //     console.log(totalTime, '-------', tempMonthlyTarget);
        // });
        // Assuming userList is an array

        if (!userList || (userList?.length <= 0)) {
            return;
        }

        async function processData() {
            // The company's own login is returned by the user-list endpoint but
            // is not a programme participant, so it is dropped up front.
            const members = userList.filter(
                (u) => !userDetails?.email || u?.email !== userDetails?.email
            );

            const records = (await Promise.all(members.map(processUser))).filter(Boolean);

            setParticipants(records);
            // Kept as user objects: the Achieved Goal tile reads .length off this.
            setTotalUserAcheivedGoal(records.filter((r) => r.achievedGoal).map((r) => r.user));
            setLoading(false);
        }
        processData();
    }, [userList, userDetails]);

    // (timeToCompleteGoalPercentage <= 0);

    const getUserActivityRecord = async (data) => {
        setLoading(true);
        let startDate = moment().startOf('month').format('YYYY-MM-DD');
        let endDate = moment().endOf('month').format('YYYY-MM-DD');
        const fitbitUrl = (data?.fitBitId) ? `${BASE_URL}/user/getfitbitexercisedata/${startDate}/${endDate}/${data?.fitBitId}` : '';
        const garminUrl = `${BASE_URL}/user/getgarminexercisedatabydate/${startDate}/${endDate}/${data?._id}`;

        let fitbitData = fitbitUrl ? await axios({
            headers: {
                "x-access-token": authStorage.getItem("auth-token")
            },
            url: fitbitUrl,
            method: 'GET'
        }) : '';

        let garminData = await axios({
            headers: {
                "x-access-token": authStorage.getItem("auth-token")
            },
            url: garminUrl,
            method: 'GET'
        });

        // Apple members were previously invisible here — this screen only ever
        // fetched Fitbit and Garmin, so an Apple user reported zero minutes while
        // their own Summary & Goals page showed real activity. summaryGoals.jsx
        // substitutes Apple data for Garmin rather than merging, so match that.
        if (data?.isAppleConnected) {
            const appleData = await axios({
                headers: {
                    "x-access-token": authStorage.getItem("auth-token")
                },
                url: `${BASE_URL}/apple/getapplehealthdatabydate/${startDate}/${endDate}/${data?._id}`,
                method: 'GET'
            }).catch(() => null);
            if (appleData) {
                garminData = appleData;
            }
        }

        fitbitData = fitbitData?.data?.data;
        garminData = garminData?.data?.data;

        setLoading(false);
        return aggregateZoneTotals(fitbitData, garminData);
    };

    return (
        <>
            <div className="md:mx-10 md:h-auto px-4 sm:px-0">
            <h2 className="mob-header font-medium mb-5 mt-5">Company/Report</h2>

                {companyList && companyList.length > 0 && (
                    <div className="flex align-center items-center justify-between border-b border-blue-200 border-opacity-20 mt-10">
                        <div className="flex items-center">
                            <div className="text-sm tracking-tight font-light text-gray-500 mr-2">
                                Select :{' '}
                            </div>
                            <DropDown selected={selected} setSelected={setSelected} dataList={companyList} firstSelected={firstSelected} />
                        </div>
                    </div>
                )}


                <div className='p-4 md:p-6 bg-white rounded-lg mt-6'>
                    <div className="text-md md:text-lg text-black">
                        Welcome, {authStorage.getItem('user-name')} !
                        <br />
                        {/* <span className='text-gray-400 mt-2 block'>Thank you for being a part of the CFT program and training. Here is your overall journey with us!</span> */}
                    </div>
                </div>
                <div className="flex flex-wrap justify-center md:py-10 py-5">
                    <div className="flex flex-grow flex-col md:mr-10 mr-0 my-2 text-center text-gray-300 bg-white rounded-lg shadow dark:border-gray-600 xl:p-8 md:p-6 p-4 dark:bg-gray-800 dark:text-white">
                        <div className="flex justify-between border-b border-blue-200 border-opacity-20 mb-8">
                            <div className="text-xl tracking-tight font-medium text-black dark:text-white">
                                Billing
                            </div>
                        </div>
                        <div className="border-b border-blue-200 border-opacity-20 pb-4">
                            <div className="mb-8">
                                <div class="flex justify-between mb-1">
                                    <span class="text-sm md:text-md text-[#0694F3] dark:text-white">User Range</span>
                                    <div>
                                        <span class="text-sm md:text-md text-[#0694F3]">{userDetails?.userRange}</span>
                                    </div>
                                </div>
                                <div class="w-full bg-[#0694F3] rounded-full h-2 dark:bg-gray-700">
                                </div>
                            </div>
                            <div className="mb-8">
                                <div class="flex justify-between mb-1 text-[#F50F51]">
                                    <span class="text-sm md:text-md">Price Per User</span>
                                    <div>
                                        <span class="text-sm md:text-md text-[#F50F51]">$ {userDetails?.price}</span>
                                    </div>
                                </div>
                                <div class="w-full bg-[#F50F51] rounded-full h-2 dark:bg-gray-700">
                                </div>
                            </div>
                            <div className="">
                                <div class="flex justify-between mb-1">
                                    <div className='text-left'>
                                        <span class="text-sm md:text-md text-[#00C98E] dark:text-white mr-2">Active CFT User</span>/
                                        <span class="text-sm md:text-md text-white-200 dark:text-white ml-2">Expected CFT User</span>
                                    </div>
                                    <div>
                                        <span class="text-sm md:text-md text-green-400 dark:text-white">{userDetails?.userCount}/</span>
                                        <span className="text-sm md:text-md text-gray-100">{userDetails?.expectedCftUser}</span>
                                    </div>
                                </div>
                                <div class="w-full bg-[#00C98E] rounded-full h-2 dark:bg-gray-400">
                                    <div class="bg-green-400 h-2 rounded-full" style={{ width: `${((userDetails?.userCount / userDetails?.expectedCftUser) * 100)}%` }}></div>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-between pt-4">
                            <div className="md:text-lg text-sm font-medium text-black dark:text-white text-left">
                                <div className="md:text-lg text-sm font-medium text-black dark:text-white">
                                Total Cost : $ {userDetails?.price && userDetails?.expectedCftUser && userDetails?.userCount ? 
                                                (parseFloat(userDetails.price) * 
                                                (Math.max(parseInt(userDetails.expectedCftUser, 10), parseInt(userDetails.userCount, 10)))) 
                                                : 0}
                                </div>
                                <div className="md:text-lg text-sm font-medium text-black dark:text-white">
                                    Total Cost (Active CFT User) : $ {userDetails?.price && userDetails?.userCount ? 
                                    Math.round(parseFloat(userDetails.price) * parseInt(userDetails.userCount, 10)) : "0"}
                                </div>
                            </div>
                            <div className="md:text-lg text-sm font-medium text-black dark:text-white text-left">
                                <div className="md:text-lg text-sm font-medium text-black dark:text-white">
                                    Discount (%)
                                </div>
                                <div className="md:text-lg text-sm font-medium text-black dark:text-white">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        placeholder="Enter discount"
                                        value={discount}
                                        style={{width: '100%'}}
                                        onChange={(e) => setDiscount(e.target.value)}
                                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                    />
                                </div>
                            </div>
                            <div onClick={() => downloadBillingReport()} className="flex text-[#0694F3] font-medium cursor-pointer md:text-base text-xs">
                                Download Report
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#0694F3" class="w-5 h-5 ml-2">
                                    <path fill-rule="evenodd" d="M12 2.25a.75.75 0 01.75.75v11.69l3.22-3.22a.75.75 0 111.06 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 111.06-1.06l3.22 3.22V3a.75.75 0 01.75-.75zm-9 13.5a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div className="flex md:w-[40%] w-[100%] flex flex-col h-[360px] my-2 text-center text-gray-300 bg-white rounded-lg shadow dark:border-gray-600 xl:p-8 md:p-6 p-4 dark:bg-gray-800 dark:text-white">
                        <div className="flex justify-between border-b border-blue-200 border-opacity-20 md:mb-8 mb-0 ">
                            <div className="text-xl tracking-tight font-medium text-black dark:text-white">
                                Summary & Goals
                            </div>
                        </div>

                        <div className="flex justify-center h-[290px] gap-10">
                            <div className="rotate-[-90deg] flex flex-col justify-center items-center ">
                                <span className="h-[20px] mb-4 text-black text-sm md:text-base">Achieved Goal : {totalUserAcheivedGoal.length}</span>
                                <span className={`${(totalUserAcheivedGoal.length === 0) ? 'md:w-[72px] w-[60px]' : `w-[${(((totalUserAcheivedGoal.length / userDetails?.userCount) * 100) * 200) / 100}px]`} h-[130px] ${(totalUserAcheivedGoal.length === 0) ? 'bg-gray-400 bg-opacity-20' : 'bg-green-100'}`}></span>
                            </div>
                            <div className="rotate-[-90deg] flex flex-col justify-center items-start ">
                                <span className="h-[20px] mb-4 text-black text-sm md:text-base">Total User : {userDetails?.userCount}</span>
                                <span className={`md:w-[72px] w-[60px] h-[130px] bg-[#0694F3]`}></span>
                            </div>
                            {/* <div className="flex flex-col justify-around">
                                <span className="rotate-[-90deg] h-[20px]">Total User</span>
                                <span className="w-[100px] h-[100px] bg-blue-400"></span>
                            </div> */}
                        </div>
                        {/* One download per reporting category: the recognition
                            list and the follow-up list are used separately. Laid
                            out on a single row so the fixed-height card does not
                            overflow. */}
                        <div className="flex flex-wrap justify-end items-center gap-x-6 gap-y-1 pt-4">
                            <div
                                onClick={() => downloadUserReport(CATEGORY_ACHIEVED)}
                                title="Download the Goal Achievers report"
                                className={`flex items-center font-medium cursor-pointer text-sm ${achieverCount ? 'text-[#0694F3]' : 'text-gray-400'}`}
                            >
                                Goal Achievers ({achieverCount})
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 ml-1.5">
                                    <path fillRule="evenodd" d="M12 2.25a.75.75 0 01.75.75v11.69l3.22-3.22a.75.75 0 111.06 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 111.06-1.06l3.22 3.22V3a.75.75 0 01.75-.75zm-9 13.5a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div
                                onClick={() => downloadUserReport(CATEGORY_SHORTFALL)}
                                title="Download the Qualified Activity - Goal Not Reached report"
                                className={`flex items-center font-medium cursor-pointer text-sm ${shortfallCount ? 'text-[#0694F3]' : 'text-gray-400'}`}
                            >
                                Goal Not Reached ({shortfallCount})
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 ml-1.5">
                                    <path fillRule="evenodd" d="M12 2.25a.75.75 0 01.75.75v11.69l3.22-3.22a.75.75 0 111.06 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 111.06-1.06l3.22 3.22V3a.75.75 0 01.75-.75zm-9 13.5a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
export default CompanyReport;
